import clsx from 'clsx';
import { AriaAttributes, FormEvent, LegacyRef, Ref, useRef } from 'react';
import { Wrapper } from './Styled';
import BaseInput, {
  Country,
  Props,
  getCountryCallingCode,
  parsePhoneNumber
} from 'react-phone-number-input';

// Mirrors the border, hover, focus, and error states of the component
// library's `TextField` so the phone input matches the other form fields.
const wrapperClassName =
  'rounded-md border transition-colors duration-200 ' +
  'focus-within:border-ds-border-focus focus-within:ring-1 focus-within:ring-ds-border-focus';

const validClassName =
  'border-ds-border-control hover:border-ds-border-control-hover';

const invalidClassName = 'border-ds-border-control-negative';

const readOnlyClassName = [
  '!bg-ds-cool-gray-2 !border-transparent hover:!border-transparent',
  '[&_input]:!bg-transparent [&_input]:cursor-default',
  '[&_.PhoneInputCountrySelect]:!opacity-0 [&_.PhoneInputCountrySelect]:cursor-default',
  '[&_.PhoneInputCountrySelectArrow]:hidden'
].join(' ');

function isAriaInvalid(value: unknown) {
  return value === true || value === 'true';
}

function getNationalDigits(value?: string, country?: Country) {
  if (!value) {
    return '';
  }

  const nationalNumber = parsePhoneNumber(value)?.nationalNumber;
  if (nationalNumber) {
    return nationalNumber as string;
  }

  const digits = value.replace(/\D/g, '');
  const callingCode = country ? getCountryCallingCode(country) : '';
  return callingCode && digits.startsWith(callingCode)
    ? digits.slice(callingCode.length)
    : '';
}

interface PhoneInputProps extends Props<{}> {
  /** Ref to the underlying phone number `<input>` element. */
  inputRef?: Ref<HTMLInputElement>;

  /** Forwarded to the nested `<input>`; also drives the wrapper's error border. */
  'aria-invalid'?: AriaAttributes['aria-invalid'];
}

function PhoneInput({
  inputRef,
  onChange,
  onCountryChange,
  ...props
}: PhoneInputProps) {
  const countryRef = useRef<Country | undefined>(undefined);
  const migratedValueRef = useRef<string | undefined>(undefined);
  const countryPickedRef = useRef(false);

  function handleChangeCapture(event: FormEvent<HTMLDivElement>) {
    countryPickedRef.current = event.target instanceof HTMLSelectElement;
  }

  function handleCountryChange(country?: Country) {
    const countryPicked = countryPickedRef.current;
    countryPickedRef.current = false;
    const nationalDigits = countryPicked
      ? getNationalDigits(props.value as string | undefined, countryRef.current)
      : '';

    migratedValueRef.current =
      country && nationalDigits
        ? `+${getCountryCallingCode(country)}${nationalDigits}`
        : undefined;
    queueMicrotask(() => {
      migratedValueRef.current = undefined;
    });

    countryRef.current = country;
    onCountryChange?.(country);
  }

  const handleChange: Props<{}>['onChange'] = value => {
    const migratedValue = migratedValueRef.current;
    migratedValueRef.current = undefined;

    const lostDigits = !getNationalDigits(value, countryRef.current);
    onChange(
      migratedValue && lostDigits ? (migratedValue as typeof value) : value
    );
  };

  // `aria-invalid` reaches the nested `<input>` either through
  // `numberInputProps` or as a top-level prop (e.g. from Semantic's
  // `FormField`), so check both to mirror it on the wrapper border.
  const isInvalid =
    isAriaInvalid(props['aria-invalid']) ||
    isAriaInvalid(
      (props.numberInputProps as AriaAttributes | undefined)?.['aria-invalid']
    );

  return (
    <Wrapper
      onChangeCapture={handleChangeCapture}
      className={clsx(
        wrapperClassName,
        isInvalid ? invalidClassName : validClassName,
        props.readOnly && readOnlyClassName
      )}
    >
      {/* react-phone-number-input's types describe this ref as pointing to
          its internal class component, but it actually forwards straight
          through to the underlying `<input>`. */}
      <BaseInput
        ref={inputRef as LegacyRef<never>}
        {...props}
        onChange={handleChange}
        onCountryChange={handleCountryChange}
      />
    </Wrapper>
  );
}

export default PhoneInput;
export type { PhoneInputProps };
